import { tentPagePanels, type Element, type PageDesign, type Product, type SavedTemplate } from '@cal/engine';

/** The design a template's preview shows: the year page, a tent face or a month page. */
export function mainDesign(designs: Partial<Record<'month' | 'year' | 'face', PageDesign>>, type: Product['productType']): PageDesign | undefined {
  return type === 'year' ? designs.year : type === 'tent' ? designs.face : designs.month;
}

const KIND: Record<Element['type'], { label: string; fill: string; stroke: string }> = {
  grid: { label: 'Calendar', fill: '#dbe7f5', stroke: '#1f3a5f' },
  yeargrid: { label: 'Calendar', fill: '#dbe7f5', stroke: '#1f3a5f' },
  image: { label: 'Image', fill: '#f3e3cf', stroke: '#b26b1f' },
  logo: { label: 'Logo', fill: '#e6e0f4', stroke: '#5b3fa8' },
  text: { label: 'Text', fill: '#ffffff', stroke: '#6b7280' },
  shape: { label: '', fill: '#eef1f5', stroke: '#c3cad3' },
  mini: { label: 'Mini', fill: '#e2efe6', stroke: '#2f7a4a' },
  key: { label: 'Key', fill: '#ffffff', stroke: '#9aa3ad' },
  legend: { label: 'Events', fill: '#ffffff', stroke: '#9aa3ad' },
  holidays: { label: 'Holidays', fill: '#fdf2f0', stroke: '#b3261e' },
};

/**
 * Schematic of a page design: where the calendar and the blocks sit, drawn
 * from the saved element rectangles (no rendering, so it is instant).
 */
export function Wireframe({ design, w, h, width = 120, title }: { design: PageDesign | undefined; w: number; h: number; width?: number; title?: string }) {
  const height = (width * h) / w;
  const fs = Math.min(w, h) / 22;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={title ?? 'Template preview'} style={{ background: '#fff', border: '1px solid #d5dae0', borderRadius: 3, flex: 'none' }}>
      {design?.elements.filter((e) => !e.hidden).map((e) => {
        const k = KIND[e.type];
        const r = e.rect;
        const label = e.type === 'image' ? `${k.label} ${Math.round(r.w)}×${Math.round(r.h)}` : e.type === 'text' ? (e.role === 'heading' ? 'Heading' : 'Text') : k.label;
        return (
          <g key={e.id}>
            <rect x={r.x} y={r.y} width={r.w} height={r.h} rx={e.type === 'image' ? e.radiusMm : 0} fill={e.type === 'shape' && e.fill ? '#eef1f5' : k.fill} stroke={k.stroke} strokeWidth={Math.min(w, h) / 250} />
            {label && r.h > fs * 1.2 && r.w > fs * 2 && (
              <text x={r.x + r.w / 2} y={r.y + r.h / 2} fontSize={Math.min(fs, r.h * 0.5, r.w / Math.max(4, label.length * 0.6))} textAnchor="middle" dominantBaseline="middle" fill={k.stroke} fontFamily="system-ui, sans-serif">{label}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Preview of a saved template (main page). */
export function SavedTemplatePreview({ t, width }: { t: SavedTemplate; width?: number }) {
  const type = t.product?.productType ?? 'monthly';
  // tent: the saved size is the flat sheet; preview the first calendar panel
  const panel = type === 'tent' && t.product?.tent ? tentPagePanels({ widthMm: t.size.w, heightMm: t.size.h, tent: t.product.tent })[0] : null;
  return <Wireframe design={mainDesign(t.designs, type)} w={panel ? panel.w : t.size.w} h={panel ? panel.h : t.size.h} width={width} />;
}
